import { useRef } from "react";
import { X } from "lucide-react";

import { Button } from "@/components/ui/Button";
import { Input, Label } from "@/components/ui/Input";

import { type RmaQuantityModelLine } from "./rmaQuantity";

const EMPTY_ROW: RmaQuantityModelLine = { quantity: "", models: "", reason: "" };

export function QualityRmaQuantityLines({
  idPrefix,
  lines,
  onChange,
  disabled = false,
  onCommit,
}: {
  idPrefix: string;
  lines: RmaQuantityModelLine[];
  onChange: (lines: RmaQuantityModelLine[]) => void;
  disabled?: boolean;
  onCommit?: (lines: RmaQuantityModelLine[]) => void;
}): JSX.Element {
  const rows = lines.length > 0 ? lines : [EMPTY_ROW];
  const rowsRef = useRef(rows);
  rowsRef.current = rows;

  function commit(next: RmaQuantityModelLine[]): void {
    onCommit?.(next);
  }

  function updateRow(index: number, patch: Partial<RmaQuantityModelLine>): void {
    const next = rowsRef.current.map((row, rowIndex) => (rowIndex === index ? { ...row, ...patch } : row));
    rowsRef.current = next;
    onChange(next);
  }

  function addRow(): void {
    const next = [...rowsRef.current, { quantity: "1", models: "", reason: "" }];
    rowsRef.current = next;
    onChange(next);
    commit(next);
  }

  function removeRow(index: number): void {
    const current = rowsRef.current;
    const next = current.length <= 1 ? [EMPTY_ROW] : current.filter((_, rowIndex) => rowIndex !== index);
    rowsRef.current = next;
    onChange(next);
    commit(next);
  }

  return (
    <div className="space-y-2 md:col-span-2">
      <div className="hidden gap-3 md:grid md:grid-cols-[5.5rem_minmax(22rem,1.6fr)_minmax(16rem,1fr)_2.5rem]">
        <Label>Quantity</Label>
        <Label>Model</Label>
        <Label>RMA reason</Label>
        <span className="sr-only">Remove row</span>
      </div>
      {rows.map((row, index) => {
        const empty = row.quantity.trim() === "" && row.models.trim() === "" && row.reason.trim() === "";
        return (
          <div
            key={`${idPrefix}-row-${index}`}
            className="grid grid-cols-1 items-end gap-2 md:grid-cols-[5.5rem_minmax(22rem,1.6fr)_minmax(16rem,1fr)_2.5rem] md:gap-3"
          >
            <div>
              <Label className="mb-1 md:sr-only" htmlFor={`${idPrefix}-quantity-${index}`}>
                Quantity
              </Label>
              <Input
                id={`${idPrefix}-quantity-${index}`}
                value={row.quantity}
                disabled={disabled}
                placeholder="1"
                inputMode="numeric"
                onChange={(event) => updateRow(index, { quantity: event.target.value })}
                onBlur={() => commit(rowsRef.current)}
              />
            </div>
            <div>
              <Label className="mb-1 md:sr-only" htmlFor={`${idPrefix}-model-${index}`}>
                Model
              </Label>
              <Input
                id={`${idPrefix}-model-${index}`}
                value={row.models}
                disabled={disabled}
                placeholder="FI-5A-W-2 S08TRB-Y"
                className="min-w-0"
                onChange={(event) => updateRow(index, { models: event.target.value })}
                onBlur={() => commit(rowsRef.current)}
              />
            </div>
            <div>
              <Label className="mb-1 md:sr-only" htmlFor={`${idPrefix}-reason-${index}`}>
                RMA reason
              </Label>
              <Input
                id={`${idPrefix}-reason-${index}`}
                value={row.reason ?? ""}
                disabled={disabled}
                placeholder="Poor battery"
                onChange={(event) => updateRow(index, { reason: event.target.value })}
                onBlur={() => commit(rowsRef.current)}
              />
            </div>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="h-10 w-10 px-0"
              disabled={disabled || (rows.length === 1 && empty)}
              aria-label={`Remove row ${index + 1}`}
              onClick={() => removeRow(index)}
            >
              <X className="h-4 w-4" aria-hidden />
            </Button>
          </div>
        );
      })}
      <Button type="button" size="sm" variant="secondary" disabled={disabled} onClick={addRow}>
        Add row
      </Button>
    </div>
  );
}
